// gTables' MCP tools (mcpTools.ts), offered to Giraffe's MCP relay. Ported from
// the schematic app's bridge, which matches chat-code's src/mcp/register.ts.
//
// The relay (mcp-relay.giraffe.build) does not reach into app frames: each app
// opens its own socket to it, signed in with the user's Giraffe token, and
// registers the tools it answers. An agent then sees them under
// `giraffe_app_tools` and calls them through `giraffe_app_call`. Wire v4:
//
//   → { t: "hello", v, token }            once the socket opens
//   → { t: "frames", frames: [frame] }    who we are (app, workspace, project) and our tools
//   ← { t: "welcome", userId, … }         the relay has checked the token
//   ← { t: "call", id, tool, args, … }    an agent's call
//   → { t: "result", id, result }
//
// Opt-in (File menu → MCP), since it holds a socket open for as long as the
// panel is. One frame id per install, so a reconnect is not a second frame. No
// anonymous fallback: without the user's token it does not connect. Dropped
// sockets come back with backoff; three refusals in a row stop it. The same
// dispatcher is on `window.__giraffeMcp` ({ call, list, tools }) for the console.

import { rpc } from '@gi-nx/iframe-sdk';
import { APP, createDispatcher, type McpHost } from './mcpTools';

export const RELAY = 'wss://mcp-relay.giraffe.build/ws';
/** Keep in step with the relay's. */
export const WIRE = 4;
export const REFUSED = 4401;

export type McpStatus =
  | { state: 'connecting' }
  | { state: 'connected'; user: string | null }
  | { state: 'dropped'; code: number; retrying: boolean }
  | { state: 'stopped'; reason: string }
  | { state: 'unavailable'; reason: string };

/** The user id inside a JWT, unverified: the relay verifies it. */
export function subjectOf(token: string): string | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const p = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))) as { user_id?: unknown };
    return typeof p.user_id === 'number' ? String(p.user_id) : typeof p.user_id === 'string' && p.user_id ? p.user_id : null;
  } catch {
    return null;
  }
}

/** The socket's ref: the first 32 hex of sha256("giraffe:<user id>"). */
export async function refFor(subject: string): Promise<string> {
  const d = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`giraffe:${subject}`));
  return [...new Uint8Array(d)]
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 32);
}

export function installMcpBridge(host: () => McpHost, onStatus: (s: McpStatus) => void = () => {}): () => void {
  const dispatcher = createDispatcher(host);
  const globals = window as unknown as Record<string, unknown>;
  globals.__giraffeMcp = dispatcher;
  const frameId = `${APP}-${Math.random().toString(36).slice(2, 8)}`;
  let socket: WebSocket | null = null;
  let closed = false;
  let attempt = 0;
  let refusals = 0;

  const connect = async (): Promise<void> => {
    if (closed) return;
    if (window.parent === window) return onStatus({ state: 'unavailable', reason: 'Not in Giraffe: MCP works in a Giraffe panel.' });
    onStatus({ state: 'connecting' });
    let token: unknown;
    let params: { activeWorkspace?: unknown; activeProject?: unknown };
    try {
      token = await rpc.invoke('getUserClaimsJwt' as never, [] as never);
      params = ((await rpc.invoke('getUrlParams' as never, [] as never)) ?? {}) as typeof params;
    } catch (e) {
      return onStatus({ state: 'unavailable', reason: `Giraffe did not answer: ${e instanceof Error ? e.message : String(e)}` });
    }
    const subject = typeof token === 'string' && token ? subjectOf(token) : null;
    if (typeof token !== 'string' || !subject)
      return onStatus({ state: 'unavailable', reason: 'Giraffe gave no sign-in token, so this panel cannot prove whose it is.' });
    const workspaceId = params.activeWorkspace ? String(params.activeWorkspace) : '';
    const projectId = params.activeProject ? String(params.activeProject) : '';
    if (!workspaceId || !projectId) return onStatus({ state: 'unavailable', reason: 'Open a saved project: MCP needs one to attach to.' });
    if (closed) return;
    const url = new URL(RELAY);
    url.searchParams.set('ref', await refFor(subject));
    const frame = {
      frameId,
      app: APP,
      workspaceId,
      projectId,
      projectUrl: `${location.ancestorOrigins?.[0] ?? ''}/v6/${workspaceId}/work/project/${projectId}`,
      tools: dispatcher.tools,
    };
    const ws = new WebSocket(url.toString());
    socket = ws;
    const send = (m: unknown): void => {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m));
    };
    ws.addEventListener('open', () => {
      send({ t: 'hello', v: WIRE, token });
      send({ t: 'frames', frames: [frame] });
    });
    ws.addEventListener('message', (e) => {
      let m: { t?: string; id?: string; tool?: string; args?: unknown; userName?: unknown };
      try {
        m = JSON.parse(String(e.data));
      } catch {
        return;
      }
      if (m.t === 'welcome') {
        attempt = 0;
        refusals = 0;
        onStatus({ state: 'connected', user: typeof m.userName === 'string' ? m.userName : null });
      } else if (m.t === 'call' && m.id && m.tool) {
        send({ t: 'result', id: m.id, result: dispatcher.call(m.tool, m.args ?? {}) });
      }
    });
    ws.addEventListener('error', () => ws.close());
    ws.addEventListener('close', (e) => {
      if (socket !== ws) return;
      socket = null;
      if (closed) return;
      refusals = e.code === REFUSED ? refusals + 1 : 0;
      if (refusals >= 3) return onStatus({ state: 'stopped', reason: e.reason || 'The relay refused this panel three times.' });
      onStatus({ state: 'dropped', code: e.code, retrying: true });
      attempt += 1;
      window.setTimeout(() => void connect(), Math.min(500 * 2 ** (attempt - 1), 15000));
    });
  };
  void connect();

  return () => {
    closed = true;
    socket?.close();
    socket = null;
    delete globals.__giraffeMcp;
  };
}
